import type {
  ApiNode,
  Filter,
  FilterGroup,
  NodeData,
} from "@/workflow_api/types/workflow";
import type { NodeVariable, Variable } from "../../types";
import type { DCConditionData } from "../components/condition";
import {
  DataCenterConditionOperations,
  type DataCenterTypeOperationTypes,
} from "../components/condition-operations";
import {
  scopeItems,
  type ScopeItem,
  type ScopeState,
} from "../components/scope-selector";
import type {
  ActionItemType,
  DataCenterSource,
  DCOperation,
  ValueColumn,
  ValueInputType,
} from "../types";
import { v4 as uuid } from "uuid";
import type { Table } from "@/queries/data-center/data-center.api";
import type { ActionItem } from "../components/action-selector";
import type { LogicalOperator } from "../../if/types";

/**
 * Maps database column types to ValueInputType
 */
const mapColumnTypeToValueInputType = (columnType: string): ValueInputType => {
  switch (columnType.toLowerCase()) {
    case "string":
    case "text":
    case "varchar":
    case "char":
      return "string";
    case "number":
    case "int":
    case "integer":
    case "float":
    case "double":
    case "decimal":
      return "number";
    case "date":
    case "datetime":
    case "timestamp":
      return "date";
    case "boolean":
    case "bool":
      return "boolean";
    case "regex":
      return "regex";
    case "positive-integer":
      return "positive-integer";
    default:
      return "string"; // fallback to string for unknown types
  }
};

/**
 * Parse backend type in uppercase to app supported types
 * @param type
 * @returns
 */
export const parseType = (type: string | undefined) => {
  if (!type) return "string";
  switch (type.toUpperCase()) {
    case "STRING":
      return "string";
    case "FLOAT":
    case "INTEGER":
    case "NUMBER":
      return "number";
    case "DECIMAL":
      return "number";
    case "BOOLEAN":
      return "boolean";
    case "DATE":
      return "date";
    case "ARRAY":
      return "array";
    default:
      return "string";
  }
};

/**
 * Check and parse value according to type
 * If parsing fails, returns empty string
 * @param value
 * @param type
 * @returns
 */
export const tryParseValueType = (
  value: string,
  type: ValueInputType,
): string => {
  switch (type) {
    case "number": {
      try {
        const parsed = Number(value);
        return isNaN(parsed) ? "" : value;
      } catch {
        return "";
      }
    }
    case "boolean": {
      try {
        if (typeof value === "boolean") {
          return String(value);
        }
        if (typeof value === "string") {
          const lowered = value.toLowerCase();
          if (lowered === "true" || lowered === "false") {
            return value;
          }
        }
        return "";
      } catch {
        return "";
      }
    }
    case "date":
      return isValidDateString(String(value)) ? value : "";
    case "string":
      return value;
    default:
      return value;
  }
};

// Helper function to get placeholder text based on column type
const getPlaceholderForType = (type: string): string => {
  switch (type) {
    case "string":
      return "Escribe un texto";
    case "number":
      return "Escribe un número";
    case "date":
      return "Selecciona una fecha";
    case "boolean":
      return "Elige verdadero o falso";
    case "variable":
      return "Elige una variable";
    case "regex":
      return "Escribe una expresión regular";
    case "positive-integer":
      return "Escribe un número entero positivo";
    default:
      return "Ingresa un valor";
  }
};

/**
 * Find a data center operation by the name
 * @param type operation data type
 * @returns
 */
const findOperation = (
  type: string,
  dataType: DataCenterTypeOperationTypes,
) => {
  const operations = DataCenterConditionOperations[dataType];
  return operations.find((op) => op.name === type);
};

/**
 * map and flatten node variables to include node_id
 *
 * @param inputVariables - List of node variables
 * @returns
 */
const flatMapVariables = (inputVariables: NodeVariable[]) => {
  return inputVariables.flatMap((node) =>
    (node.variables || []).map((v) => ({
      ...v,
      node_id: node.nodeId,
      ref_name: node.ref_name,
    })),
  );
};

/**
 * Validates if a string represents a valid date
 * @param dateString - The string to validate as a date
 * @returns true if valid date, false otherwise
 */
const isValidDateString = (dateString: string): boolean => {
  if (!dateString || typeof dateString !== "string") return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Get the scope variable information from the node
 *
 * @param scopeOptions
 * @param variables
 * @param node
 * @returns
 */
export const getDeserializedScope = (
  scopeOptions: ScopeItem[],
  nodes: Filter[],
  inputVariables: NodeVariable[],
): ScopeState => {
  const scope: ScopeState = {
    comparativeValue: "",
    operationType: "input",
    scope: {
      id: "created_at",
      name: "Fecha de creación",
      type: "dateScope",
      data_type: "date",
    },
    conditionScope: { name: "ANY", label: "Cualquiera" },
    initialRange: { value: "", type: "input" },
    endRange: { value: "", type: "input" },
    dateRangeError: "",
  };
  const node = nodes?.[0];
  if (!node) return scope;
  // search for the scope in the first inputs element
  const scopeFound = scopeOptions.find((s) => s.id === node.name);
  if (scopeFound) {
    scope.scope = scopeFound;
    const conditionFound = findOperation(node.condition, scopeFound.type);
    if (conditionFound) {
      scope.conditionScope = conditionFound;
    }
    if (node.global_type && node.value) {
      const allInputVariables = flatMapVariables(inputVariables);
      const nodeId = node.value as string;
      const variable = allInputVariables.find(
        (v) => v.node_id === nodeId && v.global_type === node.global_type,
      );
      if (variable) {
        scope.operationType = "variable";
        scope.comparativeValue = variable;
      }
    } else if (!node.value) {
      const allInputVariables = flatMapVariables(inputVariables);
      const variable = allInputVariables.find(
        (v) => v.id === node.field && v.node_id === node.connection,
      );
      if (variable) {
        scope.operationType = "variable";
        scope.comparativeValue = variable;
      }
    } else {
      // is a constant value
      if (Array.isArray(node.value) && node.value.length === 2) {
        const val1 = node.value[0] as string;
        const val2 = node.value[1] as string;
        if (isValidDateString(val1) && isValidDateString(val2)) {
          scope.initialRange.value = String(val1);
          scope.endRange.value = String(val2);
        }
      } else if (typeof node.value === "string") {
        const raw = node.value as string;
        let parsed: unknown = null;
        try {
          parsed = JSON.parse(raw);
        } catch {
          parsed = null;
        }
        if (Array.isArray(parsed) && parsed.length === 2) {
          const variables = inputVariables.flatMap((node) =>
            (node.variables || []).map((v) => ({
              ...v,
              node_id: node.nodeId,
            })),
          );
          const val1 = parsed[0] as string;
          const val2 = parsed[1] as string;
          if (isValidDateString(val1)) {
            scope.initialRange.value = String(val1);
          } else {
            // search for variable just for id

            const variable = variables.find((v) => v.id === parsed[0]);
            if (variable) {
              scope.initialRange.value = variable;
              scope.initialRange.type = "variable";
            }
          }

          if (isValidDateString(val2)) {
            scope.endRange.value = String(val2);
          } else {
            // search for variable just for id
            const variable = variables.find((v) => v.id === parsed[1]);
            if (variable) {
              scope.endRange.type = "variable";
              scope.endRange.value = variable;
            }
          }
        } else {
          if (isValidDateString(raw)) {
            scope.comparativeValue = raw;
          }
        }
      }
    }
  }
  return scope;
};

export const getDeserializedFilters = (
  columns: Variable[],
  filters: FilterGroup,
  inputVariables: NodeVariable[],
): DCConditionData[] => {
  const conditions: DCConditionData[] = [];
  if (!filters) return conditions;
  const nodeConditions = filters.conditions || [];
  nodeConditions.forEach((condition) => {
    const variable = columns.find((v) => v.name === condition.name);
    if (variable) {
      const operation = findOperation(
        condition.condition,
        variable.type as "string" | "number" | "date" | "boolean" | "array",
      ) as DCOperation;

      if (operation && variable) {
        let comparativeValue: Variable | string | null = null;
        let isVariable = false;
        let initialRangeValue: string | Variable | null = null;
        let initialIsVariable = false;
        let endRangeValue: string | Variable | null = null;
        let endIsVariable = false;
        const nodeType = parseType(condition.type);
        const isTheSameType = nodeType === variable.type;
        if (isTheSameType) {
          if (condition.global_type && condition.value) {
            const allInputVariables = flatMapVariables(inputVariables);
            const nodeId = condition.value as string;
            const selectedVariable = allInputVariables.find(
              (v) =>
                v.node_id === nodeId && v.global_type === condition.global_type,
            );
            if (selectedVariable) {
              isVariable = true;
              comparativeValue = selectedVariable;
            }
          } else if (!condition.value) {
            const allInputVariables = flatMapVariables(inputVariables);
            const sourceConnection = condition.connection;
            if (sourceConnection && typeof sourceConnection === "object") {
              const selectedVariable = allInputVariables.find((v) => {
                if (
                  sourceConnection.field_id &&
                  sourceConnection.source_node_id
                ) {
                  return (
                    v.id === sourceConnection.field_id &&
                    v.node_id === sourceConnection.source_node_id
                  );
                }
                if (sourceConnection.output_name) {
                  return v.id === sourceConnection.output_name;
                }
                return v.node_id === sourceConnection.source_node_id;
              });

              if (selectedVariable) {
                isVariable = true;
                comparativeValue = selectedVariable;
              }
            }
          } else if (!Array.isArray(condition.value)) {
            const raw = condition.value as string;
            let parsed: unknown = null;
            try {
              parsed = JSON.parse(raw);
            } catch {
              parsed = null;
            }
            if (Array.isArray(parsed) && parsed.length === 2) {
              const val1 = parsed[0] as string;
              const val2 = parsed[1] as string;
              const allInputVariables = flatMapVariables(inputVariables);
              const variable1 = allInputVariables.find(
                (v) => v.id === parsed[0],
              );
              if (variable1) {
                initialRangeValue = variable1;
                initialIsVariable = true;
              } else {
                initialRangeValue = String(val1);
              }

              // search for variable just for id
              const variable2 = allInputVariables.find(
                (v) => v.id === parsed[1],
              );
              if (variable2) {
                endRangeValue = variable2;
                endIsVariable = true;
              } else {
                endRangeValue = String(val2);
              }
            } else {
              comparativeValue = raw;
            }
          }
        }
        const newCondition: DCConditionData = {
          comparativeValue: comparativeValue ?? "",
          operationValueType: isVariable ? "variable" : "input",
          endRangeValue: endRangeValue ?? "",
          endRangeValueType: endIsVariable ? "variable" : "input",
          id: condition.id ?? uuid(),
          initialRangeValue: initialRangeValue ?? "",
          initialRangeValueType: initialIsVariable ? "variable" : "input",
          logicalOperator: "and",
          operation,
          variable: variable as Variable,
        };
        conditions.push(newCondition);
      }
    }
  });

  return conditions;
};

/**
 * Search for the table source
 *
 * @param node
 * @param tables
 * @returns
 */
export const getTableSource = (node: ApiNode | NodeData): DataCenterSource => {
  if (!node.inputs) return "sdt";
  const tableSource = node.inputs.find((input) => input.name === "source");
  if (tableSource) {
    // map source
    switch (tableSource.value) {
      case "ORQUESTADOR":
        return "Orquestador";
      case "OPERADOR":
        return "Operador";
      case "SDT":
      default:
        return "sdt";
    }
  }
  return "sdt";
};

/**
 * Search for the table
 *
 * @param node
 * @param tables
 * @returns
 */
export const getSelectedTable = (
  node: ApiNode,
  tables: Table[],
): Table | undefined => {
  if (!node.inputs) return;
  const tableInput = node.inputs.find((input) => input.name === "table");
  const source = node.inputs.find((input) => input.name === "source");
  if (tableInput) {
    return tables.find((t) => {
      if (source?.value === "ORQUESTADOR") {
        return t.id === tableInput.value;
      } else {
        return t.name === tableInput.value;
      }
    });
  }
};

/**
 * Get selected registry from node inputs for expediente digital
 * @param node
 * @param registries
 * @returns
 */
export const getSelectedRegistry = <T extends { id: string; name: string }>(
  node: ApiNode,
  registries: T[],
): T | undefined => {
  if (!node.inputs) return;
  const tableInput = node.inputs.find((input) => input.name === "table");
  if (tableInput) {
    return registries.find((r) => r.name === tableInput.value);
  }
};

export const getDeserializedNode = (
  node: ApiNode,
  columnsDef: Variable[],
  inputVariables: NodeVariable[],
): {
  executeQuery: boolean;
  isMultiple: boolean;
  filters: DCConditionData[];
  logicalOperator: LogicalOperator;
  variableName: string;
  scope: ScopeState | undefined;
  columns: ValueColumn[];
  limit: number | undefined;
} => {
  let isMultiple = false;
  let filters: DCConditionData[] = [];
  let logicalOperator: LogicalOperator = "and";
  let variableName: string = "";
  let scope = undefined;
  let valueColumns: ValueColumn[] = [];
  let limit: number = 50;

  // search for the filters
  const filterInput = node.inputs?.find((input) => input.name === "filters");
  console.log(filterInput);
  if (filterInput) {
    try {
      const raw = filterInput.filters;
      filters = getDeserializedFilters(
        columnsDef,
        raw as FilterGroup,
        inputVariables,
      );
    } catch (error) {
      console.error("Error deserializing filters:", error);
    }
  }

  //search for execute query
  const executeQueryInput = node.inputs?.find(
    (input) => input.name === "execute_query",
  );
  let executeQuery = false;
  if (executeQueryInput) {
    executeQuery = !!executeQueryInput.value;
  }

  // search for is Multiple
  const filterMultiple = node.inputs?.find(
    (input) => input.name === "is_multiple",
  );
  if (filterMultiple) {
    isMultiple = !!filterMultiple.value;
  }

  // search for logic operator
  const logicInput = node.inputs?.find(
    (input) => input.name === "logic_operator",
  );
  if (logicInput) {
    logicalOperator = logicInput.value
      ? ((logicInput.value as string).toLowerCase() as LogicalOperator)
      : "and";
  }
  // search for variable name
  const outputName = getDataCenterOutputName(node);
  if (outputName) {
    variableName = outputName;
  }

  // search for scope
  const scopeInput = node.inputs?.find((input) => input.name === "scope");
  if (scopeInput) {
    try {
      let scopeNodes: Filter[] | null = null;
      if (typeof scopeInput.value === "string") {
        scopeNodes = JSON.parse(scopeInput.value) as Filter[];
      } else if (Array.isArray(scopeInput.value)) {
        scopeNodes = scopeInput.value as Filter[];
      } else if (
        typeof scopeInput.value === "object" &&
        scopeInput.value !== null
      ) {
        // In case an object representation is passed directly as the parsed value
        // If the object is already an array-like value, coerce to Filter[]; otherwise wrap
        const maybeArray = scopeInput.value;
        scopeNodes = Array.isArray(maybeArray)
          ? (maybeArray as unknown as Filter[])
          : ([scopeInput.value] as unknown as Filter[]);
      }
      if (Array.isArray(scopeNodes)) {
        scope = getDeserializedScope(scopeItems, scopeNodes, inputVariables);
      }
    } catch (e) {
      console.error("Error deserializing scope:", e);
    }
  }

  // search for columns (create and update operations)
  // Use provided columns definition (columnsDef) to resolve variable references
  valueColumns = getDeserializedColumns(node, columnsDef, inputVariables);

  // search for limit
  const limitInput = node.inputs?.find((input) => input.name === "limit");
  if (limitInput) {
    limit = limitInput.value ? Number(limitInput.value) : 50;
  }

  return {
    isMultiple,
    filters,
    logicalOperator,
    variableName,
    scope,
    columns: valueColumns,
    limit,
    executeQuery,
  };
};

/**
 * Get Node selected action
 *
 * @param node
 * @param actions
 * @returns
 */
export const getNodeAction = (
  node: ApiNode,
  actions: ActionItem[],
): ActionItem | undefined => {
  const actionInput = node.inputs?.find((input) => input.name === "operation");
  if (actionInput) {
    const action = actions.find(
      (a) =>
        a.id?.toLowerCase() === actionInput.value?.toString().toLowerCase(),
    );
    if (action) {
      return action;
    }
  }
};

/**
 * Get Node selected action string
 *
 * @param node
 * @returns
 */
export const getNodeActionString = (
  node: ApiNode | NodeData,
): ActionItemType | undefined => {
  const actionInput = node.inputs?.find((input) => input.name === "operation");
  if (actionInput) {
    return (actionInput.value as string)?.toLowerCase() as ActionItemType;
  }
};

/**
 * Get Node output name string
 *
 * @param node
 * @returns
 */
export const getDataCenterOutputName = (
  node: ApiNode | NodeData,
): string | undefined => {
  const actionInput = node.inputs?.find(
    (input) => input.name === "output_name",
  );
  if (actionInput) {
    return actionInput.value as string;
  }
};

/**
 * Deserialize columns data for create and update operations
 *
 * @param node
 * @param columnsDef - Table column definitions
 * @param inputVariables - Input variables for resolving variable references
 * @returns
 */
export const getDeserializedColumns = (
  node: ApiNode,
  columnsDef: Variable[],
  inputVariables: NodeVariable[],
): ValueColumn[] => {
  const dataInput = node.inputs?.find((input) => input.name === "data");
  if (!dataInput || !dataInput.value) {
    return [];
  }
  try {
    let columnsData: Record<string, string>;

    // Manejar tanto string JSON como objeto directo
    if (typeof dataInput.value === "string") {
      columnsData = JSON.parse(dataInput.value) as Record<string, string>;
    } else if (
      typeof dataInput.value === "object" &&
      dataInput.value !== null
    ) {
      columnsData = dataInput.value as unknown as Record<string, string>;
    } else {
      console.error("Unexpected data format:", typeof dataInput.value);
      return [];
    }

    const columns: ValueColumn[] = [];

    // Get flat list of all input variables
    const allInputVariables = inputVariables.flatMap((node) =>
      (node.variables || []).map((v) => ({
        ...v,
        node_id: node.nodeId,
      })),
    );

    // Use table column definitions to create proper columns
    columnsDef.forEach((columnDef) => {
      const columnValue = columnsData[columnDef.name];
      let value: string | Variable = "";
      let inputType: "input" | "variable" = "input";
      const valueType = mapColumnTypeToValueInputType(columnDef.type); // use safe mapping
      if (columnValue !== undefined) {
        // Si el valor tiene el formato {{variable_name}}, es una variable
        if (
          typeof columnValue === "string" &&
          columnValue.startsWith("{{") &&
          columnValue.endsWith("}}")
        ) {
          const variableId = columnValue.slice(2, -2); // Remover {{ y }}
          const variable = allInputVariables.find((v) => v.name === variableId);
          if (variable) {
            value = variable;
            inputType = "variable";
          }
        } else {
          value = tryParseValueType(columnValue, valueType);
          inputType = "input";
        }
      }

      columns.push({
        name: columnDef.id,
        value,
        inputType,
        valueType,
        label: columnDef.name, // Use the actual column name from table definition
        placeholder: getPlaceholderForType(columnDef.type),
        isRequired: false, // You might want to check if the column has constraints
      });
    });

    return columns;
  } catch (e) {
    console.error("Error deserializing columns:", e);
    return [];
  }
};
